import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import AssignmentIcon from '@mui/icons-material/Assignment';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import React from 'react';
import PropTypes from 'prop-types';

// import CustomButton from './CustomButton';

import showcases from '../content/showcases';
import { useAppContext } from '../hooks/AppProvider';
import { trackEvent } from '../modules/matomo';
import Reveal from './Reveal';

const NavIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  background: theme.palette.background.paper,
  boxShadow: `0 0 10px 5px ${theme.palette.divider}`,
  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
  '&:focus, &:hover': {
    background: theme.palette.background.paper,
    boxShadow: `0 0 6px 3px ${theme.palette.background.brand}`,
    [theme.breakpoints.up('md')]: {
      top: '50%',
      boxShadow: `0 0 15px 7px ${theme.palette.background.brand}`,
    },
    transform: 'translateY(-50%) scale(1.05)',
  },
}));

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  justifyContent: 'center',
}));

const ShowcaseSelector = styled(ButtonBase)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  '&::before': {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: theme.palette.divider,
    content: '""',
    transition: 'background 0.3s ease-in-out, transform 0.3s ease-in-out',
  },
  '&[aria-pressed="true"]::before': {
    background: theme.palette.text.primary,
  },
  '&:hover::before, &:focus-visible::before': {
    transform: 'scale(1.3)',
  },
}));

const ShowCase = ({ type = false }) => {
  const theme = useTheme();
  const { reducedMotion } = useAppContext();
  const contentWrapperRef = React.useRef(null);
  const previousHeightRef = React.useRef(null);
  const [count, setCount] = React.useState(0);
  const [transitionKey, setTransitionKey] = React.useState(0);
  const [transitionDirection, setTransitionDirection] = React.useState('right');
  const [contentHeight, setContentHeight] = React.useState(null);
  const filteredShowcases = type ? showcases.filter(s => s.type === type) : showcases;
  const showcase = filteredShowcases.length ? filteredShowcases[count] : {};

  React.useLayoutEffect(() => {
    if (!contentWrapperRef.current || transitionKey === 0) {
      return;
    }

    const nextHeight = contentWrapperRef.current.scrollHeight;
    if (nextHeight > 0) {
      const frameId = window.requestAnimationFrame(() => {
        setContentHeight(nextHeight);
      });

      return () => window.cancelAnimationFrame(frameId);
    }

    return undefined;
  }, [count, showcase.title, showcase.description, showcase.kpis?.length, reducedMotion, transitionKey]);

  React.useEffect(() => {
    if (transitionKey === 0) {
      return;
    }

    const timer = setTimeout(() => {
      setContentHeight(null);
    }, 450);

    return () => clearTimeout(timer);
  }, [transitionKey]);

  const changeShowcase = (nextIndex, direction) => {
    if (contentWrapperRef.current) {
      previousHeightRef.current = contentWrapperRef.current.scrollHeight;
      setContentHeight(previousHeightRef.current);
    }

    setTransitionDirection(direction);
    setCount(nextIndex);
    setTransitionKey((prev) => prev + 1);
  };

  const handleNext = () => {
    changeShowcase((count + 1) % filteredShowcases.length, 'right');
    trackEvent('Showcase', 'Nächste Fallstudie', `ShowCase ${type || 'Home'}`);
  };

  const handlePrev = () => {
    changeShowcase((count - 1 + filteredShowcases.length) % filteredShowcases.length, 'left');
    trackEvent('Showcase', 'Vorherige Fallstudie', `ShowCase ${type || 'Home'}`);
  };

  const handleSelect = (index) => {
    if (index === count) {
      return;
    }

    changeShowcase(index, index > count ? 'right' : 'left');
  };
  
  return (
    <Reveal>
      <Box className="content center" sx={{
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(2),
      background: theme.palette.background.default,
      boxShadow: `0 0 20px 10px ${theme.palette.divider}`,
      padding: theme.spacing(4),
      position: 'relative',
      h2: {
        fontSize: '1.2em',
        [theme.breakpoints.up('md')]: {
          fontSize: 'max(1.5em, 2vw)',
        },
      },
    }}>
      {filteredShowcases.length > 1 ? (
        <NavIconButton
          aria-label="Vorherige Fallstudie"
          onClick={handlePrev}
          sx={{
            left: 0,
            [theme.breakpoints.up('md')]: {
              left: '-5vw',
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '5vw' }} />
        </NavIconButton>
      ) : null}
      {filteredShowcases.length > 1 ? (
        <NavIconButton
          aria-label="Nächste Fallstudie"
          onClick={handleNext}
          sx={{
            right: 0,
            [theme.breakpoints.up('md')]: {
              right: '-5vw',
            },
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: '5vw' }} />
        </NavIconButton>
      ) : null}
      <Box
        sx={{
          height: contentHeight ? `${contentHeight}px` : 'auto',
          transition: reducedMotion ? 'none' : 'height 420ms cubic-bezier(0.16, 1, 0.3, 1)',
          overflow: 'hidden',
        }}
      >
        <Box
          ref={contentWrapperRef}
          sx={{
            height: 'auto',
            display: 'flow-root',
          }}
        >
          <Box
            key={`${count}-${transitionKey}`}
            id="showcase-content"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            sx={{
              animation: reducedMotion ? 'none' : `${transitionDirection === 'left' ? 'showcase-slide-left' : 'showcase-slide-right'} 700ms cubic-bezier(0.16, 1, 0.3, 1)`,
              willChange: 'transform, opacity',
              '@keyframes showcase-slide-right': {
                from: {
                  opacity: 0,
                  transform: 'translate3d(96px, 0, 0) scale(0.98)',
                },
                to: {
                  opacity: 1,
                  transform: 'translate3d(0, 0, 0) scale(1)',
                },
              },
              '@keyframes showcase-slide-left': {
                from: {
                  opacity: 0,
                  transform: 'translate3d(-96px, 0, 0) scale(0.98)',
                },
                to: {
                  opacity: 1,
                  transform: 'translate3d(0, 0, 0) scale(1)',
                },
              },
            }}
          >
            <h2>{showcase.title}</h2>
            <p dangerouslySetInnerHTML={{ __html: showcase.description }} />
            <ChipContainer>
              {showcase.kpis?.map((kpi, index) => (
                <Chip key={index} label={kpi.label} variant="outlined" />
              ))}
            </ChipContainer>
          </Box>
        </Box>
      </Box>
      {/* <CustomButton
        onClick={() => console.log('open showcase '+ showcase.title)}
        startIcon={<AssignmentIcon />}
        sx={{
          background: theme.palette.background.paper,
          marginTop: '2rem',
        }}
      >
        Mehr erfahren
      </CustomButton> */}
        {filteredShowcases.length > 1 ? (
        <Box sx={{
          position: 'absolute',
          bottom: '-2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: theme.spacing(1),
        }}
        role="group"
        aria-label="Fallstudie auswählen"
        >
          {filteredShowcases.map((item, index) => (
            <ShowcaseSelector
              key={index}
              aria-controls="showcase-content"
              aria-label={`Fallstudie ${index + 1}: ${item.title} anzeigen`}
              aria-pressed={index === count}
              onClick={() => handleSelect(index)}
            />
          ))}
        </Box>
        ) : null}
      </Box>
    </Reveal>
  );
};

ShowCase.propTypes = {
  type: PropTypes.string,
};

export default ShowCase;
